import { createApi } from '@reduxjs/toolkit/query/react';
import B2ChatClient, { apiResource, b2chatBaseQuery } from '@src/client';

export type UsernameStatus = 'approved' | 'reserved';

export type GetUsernameRequest = {
  botId: string;
};

export type GetUsernameResponse = {
  username: string | null;
  status: UsernameStatus | null;
};

export type GetUsernameSuggestionsRequest = {
  botId: string;
};

export type GetUsernameSuggestionsResponse = {
  suggestions: string[];
};

export type UpsertUsernameRequest = {
  botId: string;
  username: string;
};

export type UpsertUsernameResponse = {
  username: string;
  status: UsernameStatus;
};

export type DeleteUsernameRequest = {
  botId: string;
};

export type DeleteUsernameResponse = {
  success: boolean;
};

export type UsernameErrorCode =
  | 'USERNAME_REQUIRED'
  | 'USERNAME_INVALID_LENGTH'
  | 'USERNAME_INVALID_CHARS'
  | 'USERNAME_INVALID_DOT_BOUNDARY'
  | 'USERNAME_CONSECUTIVE_DOTS'
  | 'USERNAME_STARTS_WITH_WWW'
  | 'USERNAME_ENDS_WITH_DOMAIN'
  | 'USERNAME_NO_LETTER'
  | 'USERNAME_NOT_AVAILABLE'
  | 'ACCOUNT_TIER_NOT_ELIGIBLE'
  | 'NO_PERMISSION'
  | 'USERNAME_NOT_AUTHORIZED'
  | 'USERNAME_META_ERROR'
  | 'USERNAME_GET_ERROR'
  | 'USERNAME_SET_ERROR'
  | 'USERNAME_DELETE_ERROR'
  | 'USERNAME_SUGGESTIONS_ERROR'
  | 'PROVIDER_NOT_SUPPORTED'
  | 'BOT_ACCOUNT_NOT_FOUND'
  | 'BOT_NOT_FOUND'
  | 'UNAUTHORIZED_PETITION'
  | 'OPERATION_FAILED';

export type UsernameErrorResponse = {
  timestamp: number;
  errorCode: UsernameErrorCode;
  details: string;
  traceId: string;
};

const whatsappUsernameApi = createApi({
  reducerPath: 'whatsappUsernameApi',
  baseQuery: b2chatBaseQuery,
  tagTypes: ['username'],
  endpoints: ({ query, mutation }) => ({
    getUsername: query<GetUsernameResponse, GetUsernameRequest>({
      providesTags: (_result, _error, { botId }) => [
        { type: 'username', id: botId },
      ],
      extraOptions: { maxRetries: 0 },
      query: ({ botId }) =>
        apiResource(B2ChatClient.resources.whatsapp.actions.getUsername, {
          params: { botId },
          withJWT: true,
        }),
    }),

    getUsernameSuggestions: query<
      GetUsernameSuggestionsResponse,
      GetUsernameSuggestionsRequest
    >({
      extraOptions: { maxRetries: 0 },
      query: ({ botId }) =>
        apiResource(
          B2ChatClient.resources.whatsapp.actions.getUsernameSuggestions,
          { params: { botId }, withJWT: true }
        ),
    }),

    upsertUsername: mutation<UpsertUsernameResponse, UpsertUsernameRequest>({
      invalidatesTags: (_result, error, { botId }) =>
        error ? [] : [{ type: 'username', id: botId }],
      extraOptions: { maxRetries: 0 },
      query: ({ botId, username }) =>
        apiResource(B2ChatClient.resources.whatsapp.actions.upsertUsername, {
          params: { botId },
          data: { username },
          withJWT: true,
        }),
    }),

    deleteUsername: mutation<DeleteUsernameResponse, DeleteUsernameRequest>({
      invalidatesTags: (_result, error, { botId }) =>
        error ? [] : [{ type: 'username', id: botId }],
      extraOptions: { maxRetries: 1 },
      query: ({ botId }) =>
        apiResource(B2ChatClient.resources.whatsapp.actions.deleteUsername, {
          params: { botId },
          withJWT: true,
        }),
    }),
  }),
});

export const {
  useGetUsernameQuery,
  useLazyGetUsernameQuery,
  useGetUsernameSuggestionsQuery,
  useLazyGetUsernameSuggestionsQuery,
  useUpsertUsernameMutation,
  useDeleteUsernameMutation,
} = whatsappUsernameApi;

export default whatsappUsernameApi;
